import React, { useState } from 'react';
import { ChevronLeft, MapPin, Navigation, QrCode, Layers, User } from 'lucide-react';
import C from '../../constants/colors';
import { EMERGENCY_META } from '../../constants/meta';
import StatusDot from '../ui/StatusDot';
import AlertCard from '../ui/AlertCard';
import { RealQRCode, qrEncodeToCanvas } from '../../utils/qr';
import * as employeesApi from '../../api/employees';
import HseTrainingTab from './HseTrainingTab';

export default function EmployeeDetailView({ employee, company, site, zone, alerts, onBack, onEdit, onRemove, onSimulateLivePOB, onMarkEvacuated, onSimulateScan, onAcknowledge }) {
  const empAlerts = alerts.filter(a => a.employeeId === employee.id);
  const [lightbox, setLightbox] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [phoneVerified, setPhoneVerified] = useState(employee.phoneVerified ?? false);
  const [otpPhase, setOtpPhase] = useState(null); // null | 'sending' | 'awaiting_code' | 'confirming'
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');

  async function handlePrintBadge() {
    const qrCanvas = document.createElement('canvas');
    await qrEncodeToCanvas(window.location.origin + '/?e=' + employee.qrHash, qrCanvas, 160);
    const qrDataUrl = qrCanvas.toDataURL();

    // HTML-escape helper to prevent injection
    const esc = s => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

    const photoHtml = employee.photoUrl
      ? `<img src="${esc(employee.photoUrl)}" class="photo" />`
      : `<div class="photo-placeholder">${esc(employee.name.charAt(0).toUpperCase())}</div>`;

    const pinSvg = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#009688"/><circle cx="12" cy="9" r="2.8" fill="white"/></svg>`;

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Badge – ${esc(employee.name)}</title>
<style>
  @page { size: 85mm 54mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #e8e8e8; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
  .badge { background: #fff; width: 85mm; height: 54mm; padding: 3mm 4mm; overflow: hidden; box-shadow: 0 4px 16px rgba(0,0,0,0.15); border-radius: 2mm; display: flex; flex-direction: column; gap: 2mm; }
  .header { display: flex; align-items: center; gap: 1.5mm; }
  .co-name { font-size: 10pt; font-weight: 700; color: #111; line-height: 1.2; border-bottom: 0.7mm solid #009688; padding-bottom: 0.6mm; }
  .body { display: flex; justify-content: space-between; align-items: flex-start; gap: 3mm; flex: 1; }
  .fields { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: space-around; }
  .field { margin-bottom: 1.5mm; }
  .field:last-child { margin-bottom: 0; }
  .fl { color: #999; font-size: 5pt; font-weight: 600; margin-bottom: 0.4mm; text-transform: uppercase; letter-spacing: 0.06em; }
  .fv { color: #111; font-size: 8.5pt; font-weight: 700; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .right-col { display: flex; flex-direction: column; gap: 1.5mm; flex-shrink: 0; }
  .photo { width: 21mm; height: 21mm; object-fit: cover; object-position: top; border-radius: 1.5mm; display: block; }
  .photo-placeholder { width: 21mm; height: 21mm; background: #e8e8e8; border-radius: 1.5mm; display: flex; align-items: center; justify-content: center; font-size: 16pt; font-weight: 700; color: #bbb; }
  .qr-wrap { background: #fff; padding: 1mm; border: 0.4mm solid #ddd; border-radius: 1.5mm; display: inline-block; }
  .qr { width: 16mm; height: 16mm; display: block; }
  @media print { body { background: #fff; min-height: unset; } .badge { box-shadow: none; } }
</style>
</head>
<body>
<div class="badge">
  <div class="header">
    ${pinSvg}
    <span class="co-name">${esc(company?.name || 'SafeCheck')}</span>
  </div>
  <div class="body">
    <div class="fields">
      <div class="field"><div class="fl">Name:</div><div class="fv">${esc(employee.name)}</div></div>
      <div class="field"><div class="fl">ID Number:</div><div class="fv">${esc(employee.employeeId)}</div></div>
      <div class="field"><div class="fl">Job Title:</div><div class="fv">${esc(employee.department || employee.role)}</div></div>
    </div>
    <div class="right-col">
      ${photoHtml}
      <div class="qr-wrap"><img src="${qrDataUrl}" class="qr" /></div>
    </div>
  </div>
</div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 600);
    }
  }

  async function handleSendCode() {
    setOtpPhase('sending');
    setOtpError('');
    try {
      await employeesApi.verifyPhoneSend(employee.id);
      setOtpPhase('awaiting_code');
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Failed to send code');
      setOtpPhase(null);
    }
  }

  async function handleConfirmCode() {
    if (otpCode.length < 6) return;
    setOtpPhase('confirming');
    setOtpError('');
    try {
      await employeesApi.verifyPhoneConfirm(employee.id, otpCode);
      setPhoneVerified(true);
      setOtpPhase(null);
      setOtpCode('');
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Invalid or expired code');
      setOtpPhase('awaiting_code');
    }
  }

  const statusColors = { SAFE: C.green, NEED_HELP: C.red, NOT_RESPONDED: C.amber, EVACUATED: C.textSecondary };
  const statusColor = statusColors[employee.status] || C.textMuted;

  const locationStr = employee.currentSiteId
    ? [company?.name, site?.name, zone?.name].filter(Boolean).join(' → ')
    : 'Off-site / Unknown';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ChevronLeft size={16} /><span style={{ fontSize: '13px' }}>Back</span>
        </button>
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary, flex: 1 }}>{employee.name}</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onEdit} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
          <button onClick={onRemove} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.amber}66`, background: 'transparent', color: C.amber, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Archive</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, padding: '0 24px', background: C.surface }}>
        {(['overview', ...(company?.hseTrainingEnabled ? ['hse'] : [])]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: 'none', border: 'none', padding: '10px 16px', fontSize: '12px', fontWeight: 600,
              color: activeTab === tab ? C.textPrimary : C.textMuted,
              borderBottom: activeTab === tab ? `2px solid ${C.blue}` : '2px solid transparent',
              cursor: 'pointer', marginBottom: '-1px',
            }}
          >
            {tab === 'overview' ? 'Overview' : 'HSE Training'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {activeTab === 'hse' && company?.hseTrainingEnabled && (
          <HseTrainingTab employeeId={employee.id} companyId={employee.companyId} siteId={site?.id} />
        )}
        {activeTab === 'overview' && <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '24px', alignItems: 'start' }}>
          {/* Left: QR + status actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '210px' }}>
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Employee QR Badge</div>
              <div style={{ background: '#fff', borderRadius: '8px', padding: '10px', display: 'inline-block', marginBottom: '10px' }}>
                <RealQRCode value={window.location.origin + '/?e=' + employee.qrHash} size={140} darkColor='#0a0e17' lightColor='#ffffff' />
              </div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: C.textSecondary, marginBottom: '12px' }}>{employee.qrHash}</div>
              <button onClick={handlePrintBadge} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: `1px solid ${C.blue}44`, background: C.blue+'22', color: C.blue, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <QrCode size={13} />Print Badge
              </button>
            </div>

            {/* Status + actions */}
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <StatusDot status={employee.status} size={12} />
                <span style={{ fontSize: '14px', fontWeight: 700, color: statusColor }}>{employee.status.replace('_',' ')}</span>
              </div>
              {employee.emergencyType && (
                <div style={{ fontSize: '12px', color: EMERGENCY_META[employee.emergencyType]?.color || C.amber, marginBottom: '10px', fontWeight: 600 }}>
                  {EMERGENCY_META[employee.emergencyType]?.label || employee.emergencyType}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <button onClick={() => onSimulateLivePOB(employee)} style={{ padding: '8px', borderRadius: '8px', border: `1px solid ${C.blue}44`, background: C.blue+'22', color: C.blue, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}><Navigation size={12} />Simulate LivePOB</button>
                <button onClick={() => onMarkEvacuated(employee.id)} style={{ padding: '8px', borderRadius: '8px', border: `1px solid ${C.textMuted}44`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Mark Evacuated</button>
                <button onClick={() => onSimulateScan(employee)} style={{ padding: '8px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}><Layers size={12} />Zone Management</button>
              </div>
            </div>
          </div>

          {/* Right: info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Identity */}
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Identity</div>
                <div
                  onClick={() => employee.photoUrl && setLightbox(true)}
                  style={{ width: '56px', height: '56px', borderRadius: '50%', background: C.surface, border: `2px solid ${C.border}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: employee.photoUrl ? 'zoom-in' : 'default' }}>
                  {employee.photoUrl
                    ? <img src={employee.photoUrl} alt={employee.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <User size={22} color={C.textMuted} />}
                </div>
                {lightbox && (
                  <div onClick={() => setLightbox(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, cursor: 'zoom-out' }}>
                    <img src={employee.photoUrl} alt={employee.name} style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '12px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} />
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {[
                  ['Employee ID', employee.employeeId, true],
                  ['Department', employee.department, false],
                  ['Role', employee.role, false],
                  ['Company', company?.name || '—', false],
                  ['Home Site', site?.name || '—', false],
                  ['Team Size', employee.teamSize, false],
                ].map(([label, val, mono]) => (
                  <div key={label}>
                    <div style={{ fontSize: '10px', color: C.textMuted, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                    <div style={{ fontSize: '13px', color: C.textPrimary, fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit' }}>{val}</div>
                  </div>
                ))}
              </div>
              {employee.hseProfiler && (
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>HSE Profiler</div>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: C.blue, background: C.blue + '22', padding: '2px 8px', borderRadius: '4px' }}>Active</span>
                </div>
              )}
              {employee.teamMembers?.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '10px', color: C.textMuted, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Team Members</div>
                  <div style={{ fontSize: '13px', color: C.textSecondary }}>{employee.teamMembers.join(', ')}</div>
                </div>
              )}
            </div>

            {/* Contact */}
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact</div>
              {/* Phone — with verification badge */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', color: C.textMuted, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Phone</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace' }}>{employee.phone || '—'}</span>
                  {employee.phone && (
                    phoneVerified
                      ? <span style={{ fontSize: '10px', background: C.green + '22', color: C.green, padding: '2px 7px', borderRadius: '4px', fontWeight: 600 }}>Verified</span>
                      : <>
                          <span style={{ fontSize: '10px', background: C.amber + '22', color: C.amber, padding: '2px 7px', borderRadius: '4px', fontWeight: 600 }}>Unverified</span>
                          <button
                            onClick={handleSendCode}
                            disabled={otpPhase === 'sending'}
                            style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', border: `1px solid ${C.blue}44`, background: C.blue + '22', color: C.blue, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {otpPhase === 'sending' ? 'Sending…' : 'Send Code'}
                          </button>
                        </>
                  )}
                </div>
              </div>

              {/* Emergency Contact + Emergency Phone */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {[['Emergency Contact', employee.emergencyContact, false], ['Emergency Phone', employee.emergencyPhone, true]].map(([label, val, mono]) => (
                  <div key={label}>
                    <div style={{ fontSize: '10px', color: C.textMuted, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                    <div style={{ fontSize: '13px', color: C.textPrimary, fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Location */}
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Location</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <MapPin size={14} color={employee.currentSiteId ? C.blue : C.textMuted} />
                <span style={{ fontSize: '14px', fontWeight: 600, color: employee.currentSiteId ? C.textPrimary : C.textMuted }}>{locationStr}</span>
              </div>
              {employee.gps && (
                <div style={{ fontSize: '12px', color: C.blue, fontFamily: 'JetBrains Mono, monospace' }}>
                  GPS: {employee.gps.lat.toFixed(6)}, {employee.gps.lng.toFixed(6)}
                </div>
              )}
            </div>

            {/* Location History */}
            {employee.locationHistory?.length > 0 && (
              <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location History</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[...employee.locationHistory].reverse().slice(0, 10).map((ev, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: ev.action === 'ENTER_ZONE' ? C.green : C.amber, marginTop: '5px', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '12px', color: C.textPrimary }}>
                          {ev.action.replace('_',' ')} &mdash; {ev.zoneName || ev.siteName || '—'}
                        </div>
                        <div style={{ fontSize: '11px', color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
                          {new Date(ev.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Alerts */}
            {empAlerts.length > 0 && (
              <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alert History ({empAlerts.length})</div>
                {empAlerts.map(a => <AlertCard key={a.id} alert={a} onAcknowledge={onAcknowledge} />)}
              </div>
            )}
          </div>
        </div>}
      </div>

      {/* OTP verification modal */}
      {(otpPhase === 'awaiting_code' || otpPhase === 'confirming') && (
        <div
          onClick={() => { setOtpPhase(null); setOtpCode(''); setOtpError(''); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '28px', width: '360px', maxWidth: '90vw' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary, marginBottom: '8px' }}>Verify Phone</h3>
            <p style={{ fontSize: '13px', color: C.textSecondary, marginBottom: '20px', lineHeight: 1.5 }}>
              Enter the 6-digit code sent to <strong>{employee.phone}</strong>
            </p>
            <input
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoFocus
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.surface, color: C.textPrimary, fontSize: '24px', letterSpacing: '0.3em', textAlign: 'center', fontFamily: 'JetBrains Mono, monospace', marginBottom: '8px', boxSizing: 'border-box' }}
            />
            {otpError && <div style={{ fontSize: '12px', color: C.red, marginBottom: '8px' }}>{otpError}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button
                onClick={() => { setOtpPhase(null); setOtpCode(''); setOtpError(''); }}
                style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                onClick={handleConfirmCode}
                disabled={otpCode.length < 6 || otpPhase === 'confirming'}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: otpCode.length < 6 ? 0.5 : 1 }}>
                {otpPhase === 'confirming' ? 'Verifying…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
